import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Field, makeStyles, tokens, Tooltip } from '@fluentui/react-components'
import { AddFilled, AddRegular, ArrowDownloadFilled, ArrowDownloadRegular, bundleIcon } from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import useModules from '../../../hooks/module/useModules'
import useRoles, { Role } from '../../../hooks/useRoles'
import { AppAction } from '../../../config/appConfig'
import { MenuItemKey } from '../../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { ModuleType } from '../../../context/GlobalContext'
import AttributesList from '../../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../../hooks/useAxios'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { SortDirection } from '../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../../BaseOverview/BaseOverview'
import { BaseOverviewFilterType } from '../../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../BaseOverview/BaseSortMenu'
import { InvoiceOverviewDto, InvoiceStatus, InvoiceType } from '../../../config/finance/invoiceConfig'
import DateRange from '../../../components/DatePicker/DateRange'
import FilePicker from '../../../components/TagPicker/FilePicker'
import InvoiceStatusPicker from '../../../components/TagPicker/InvoiceStatusPicker'
import BooleanCombobox from '../../../components/Combobox/BooleanCombobox'
import { DateTime } from 'luxon'
import { parseDate } from '../../../helpers/dateHelper'
import UserPicker from '../../../components/TagPicker/UserPicker'
import InvoiceListItem from './InvoiceListItem'
import InvoiceHeaderItem from './InvoiceHeaderItem'
import { parseNumber } from '../../../helpers/numberHelper'
import { PriceDto, PricePrecedence } from '../../../config/finance/financeConfig'

export const useItemStyles = makeStyles({
    listInvoiceItem: {
        gridTemplateColumns: '24px 24px 24px minmax(80px, 100px) 70px auto 100px 80px 24px',

        '& button[aria-label="hover"]': {
            display: 'none',
            marginLeft: tokens.spacingVerticalS,
            height: '24px',
        },

        '&:hover': {
            '& a, & a span': {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            },

            '& button[aria-label="hover"]': {
                display: 'inline-grid',
            },
        },
    },
    listInvoiceItemMedium: {
        gridTemplateColumns: '24px 24px 24px minmax(80px, 100px) 70px auto 100px 80px 80px 80px 24px',
    },
    listInvoiceItemSmall: {
        gridTemplateColumns: '40px 5fr',
        gridTemplateRows: 'auto 1fr',
    },
    listInvoiceItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
    },
    listInvoiceItemTitle: {
        display: 'grid',
        gridTemplateColumns: '1fr 80px',
    },
    listInvoiceItemPaidIcon: {
        height: '24px',
        width: '14px',
    },
    turnoverContainer: {
        display: 'grid',
        gridTemplateColumns: 'auto 24px 80fr',
        columnGap: tokens.spacingVerticalS,
    },
})

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)

// Base item which contains all the fields to support both line item and header item.
export type BaseInvoiceItemDetails = {
    type: InvoiceType
    price: PriceDto
    outstandingPrice?: PriceDto
    unsettledPrice?: PriceDto

    // Is calculated during runtime and not actually returned by the API.
    dueDate: Date
    isDue: boolean
    paidPrice: string
    isPartiallyPaid: boolean
    isPaid: boolean
}

export type ExtendedInvoiceOverviewDto = InvoiceOverviewDto & BaseInvoiceItemDetails

export type InvoiceLineItemProps = BaseOverviewLineItemProps<ExtendedInvoiceOverviewDto, InvoiceFilterType, InvoiceSortingType>

export type InvoiceOverviewCount = BaseOverviewCount & {
    currencies: InvoiceCurrencyOverviewCount[]
}

export type InvoiceCurrencyOverviewCount = BaseInvoiceItemDetails & {
    currency: string
    subtotal: string
    total: string
    outstandingTotal: string
}

export type InvoiceFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        files?: string[]
        fileOwners?: string[]
        statuses?: InvoiceStatus[]
        hasTransaction?: boolean
    }

type InvoiceSorting = {
    id: string
    number: string
    date: string
    'recipient.name': string
    'outstandingPrice.total': string
    'files.reference': string
}

export type InvoiceSortingType = BaseOverviewSortingType<InvoiceSorting>

export function InvoiceFilter() {
    const { values, setFieldValue } = useFormikContext<InvoiceFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('invoice.filter.file')} orientation={orientation}>
                <FilePicker
                    selectedOptions={values.files || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                />
            </Field>
            <Field label={translate('invoice.filter.owner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.fileOwners || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('fileOwners', selectedOptions)}
                />
            </Field>
            <Field label={translate('invoice.status.label')} orientation={orientation}>
                <InvoiceStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions: InvoiceStatus[]) => updateFieldValue('statuses', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Accounting) && (
                <Field label={translate('invoice.filter.hasTransaction')} orientation={orientation}>
                    <BooleanCombobox
                        required
                        selectedOptions={[values.hasTransaction?.toString() || 'mixed']}
                        onOptionSelect={(_, data) => updateFieldValue('hasTransaction', data.checked === 'mixed' ? undefined : data.checked)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('invoice.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => setFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const headerSortingItems: InvoiceSortingType[] = [
    {
        property: ['number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.numberasc',
    },
    {
        property: ['number'],
        direction: SortDirection.Descending,
        default: true,
        descriptionKey: 'sorting.numberdesc',
    },
    {
        property: ['date', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.dateasc',
    },
    {
        property: ['date', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.datedesc',
    },
    {
        property: ['recipient.name', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.az',
    },
    {
        property: ['recipient.name', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.za',
    },
    {
        property: ['outstandingPrice.total', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.priceasc',
    },
    {
        property: ['outstandingPrice.total', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.pricedesc',
    },
    {
        property: ['files.reference', 'number'],
        direction: SortDirection.Ascending,
        descriptionKey: 'sorting.referenceasc',
    },
    {
        property: ['files.reference', 'number'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

export function HeaderActionsButtons() {
    const history = useHistory()
    const overviewStyles = useOverviewStyles()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<InvoiceFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<InvoiceFilterType>(DocumentGenerationType.InvoiceXls)

    return (
        <>
            <Tooltip withArrow relationship={'label'} content={translate('action.create.invoice.title')}>
                <Button icon={<AddIcon />} appearance={'primary'} onClick={() => history.push('/invoice/add')} />
            </Tooltip>
            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
            <Button appearance={'secondary'} className={overviewStyles.hidden} />
        </>
    )
}

export default function InvoiceOverview() {
    const axios = useAxios()
    const dispatch = useDispatch()
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])
    const listItemMedium = useMemo<boolean>(() => isViewport('>=', Viewport.XXL), [viewport])

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Invoice] })
    }, [])

    const defaultFilterValues = useMemo<InvoiceFilterType>(
        () => ({
            from: DateTime.local().startOf('year').minus({ year: 3 }).toISODate(),
            to: undefined,
            statuses: [InvoiceStatus.Draft, InvoiceStatus.Final, InvoiceStatus.Settled, InvoiceStatus.Credited],
            hasTransaction: null,
            attributes: [],
            query: '',
        }),
        [],
    )

    const countItems = (column: number, filterValues?: InvoiceFilterType): Promise<InvoiceOverviewCount> => {
        return axios
            .get<InvoiceOverviewCount>('/finance/invoice/count', { params: parseFilterValuesToParams(filterValues) })
            .then((response: AxiosResponse<InvoiceOverviewCount>): InvoiceOverviewCount => {
                return {
                    ...response.data,
                    currencies: response.data.currencies.map(currency => {
                        const extendedItem: InvoiceCurrencyOverviewCount = {
                            ...currency,
                            type: InvoiceType.Regular,
                            price: {
                                precedence: PricePrecedence.Total,
                                currency: currency.currency,
                                vat: [
                                    {
                                        id: '0',
                                        type: 'NL_NORMAL',
                                        value: (parseNumber(currency.total, 'en') - (parseNumber(currency.subtotal, 'en') || 0)).toString(),
                                    },
                                ],
                                subtotal: currency.subtotal,
                                total: currency.total,
                            },
                            outstandingPrice: {
                                precedence: PricePrecedence.Total,
                                currency: currency.currency,
                                vat: [
                                    {
                                        id: '0',
                                        type: 'NL_NORMAL',
                                        value: (parseNumber(currency.total, 'en') - (parseNumber(currency.subtotal, 'en') || 0)).toString(),
                                    },
                                ],
                                subtotal: undefined,
                                total: currency.outstandingTotal,
                            },
                            unsettledPrice: undefined,

                            dueDate: new Date(),
                            isDue: false,
                            paidPrice: (parseNumber(currency.total, 'en') - (parseNumber(currency.outstandingTotal, 'en') || 0)).toString(),
                            isPartiallyPaid: false,
                            isPaid: false,
                        }

                        return {
                            ...extendedItem,
                            isPartiallyPaid:
                                parseNumber(extendedItem.paidPrice, 'en') > 0 &&
                                parseNumber(extendedItem.paidPrice, 'en') < parseNumber(extendedItem.price.total, 'en'),
                            isPaid: (parseNumber(extendedItem.outstandingPrice?.total, 'en') || 1) === 0,
                        }
                    }),
                }
            })
    }

    const loadItems = (
        column: number,
        filterValues?: InvoiceFilterType,
        sortingValues?: InvoiceSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedInvoiceOverviewDto[]> => {
        return axios
            .get<InvoiceOverviewDto[]>('/finance/invoice', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<InvoiceOverviewDto[]>): ExtendedInvoiceOverviewDto[] =>
                (response?.data || []).map((item: InvoiceOverviewDto) => {
                    const dueDate = parseDate(item.date).plus({ days: item.term || 0 })
                    const diffDate = dueDate.diff(DateTime.now().startOf('day')).as('days')
                    const extendedItem: ExtendedInvoiceOverviewDto = {
                        ...item,
                        dueDate: dueDate.toBSON(),
                        isDue: diffDate <= 0,
                        paidPrice: (parseNumber(item.price.total, 'en') - (parseNumber(item.outstandingPrice?.total, 'en') || 0)).toString(),
                        isPartiallyPaid: false,
                        isPaid: false,
                    }

                    return {
                        ...extendedItem,
                        isPartiallyPaid:
                            parseNumber(extendedItem.paidPrice, 'en') > 0 &&
                            parseNumber(extendedItem.paidPrice, 'en') < parseNumber(extendedItem.price.total, 'en'),
                        isPaid: (parseNumber(extendedItem.outstandingPrice?.total, 'en') || 1) === 0,
                    }
                }),
            )
    }

    return (
        <BaseOverview<ExtendedInvoiceOverviewDto, InvoiceFilterType, InvoiceSortingType, InvoiceOverviewCount>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listClassnames={[overviewStyles.listXXXL]}
            listItemHeaderContent={InvoiceHeaderItem}
            listItemContent={props => <InvoiceListItem {...props} />}
            listItemClassnames={(viewType, item) => {
                return [
                    itemStyles.listInvoiceItem,
                    listItemSmall && itemStyles.listInvoiceItemSmall,
                    listItemMedium && itemStyles.listInvoiceItemMedium,
                    item &&
                        item.isDue &&
                        ![InvoiceStatus.Done, InvoiceStatus.CreditedDone, InvoiceStatus.SettledDone].includes(item.status) &&
                        overviewStyles.colorRed,
                ]
            }}
            headerActionsButtons={HeaderActionsButtons}
            headerSortingItems={headerSortingItems}
            filterContent={InvoiceFilter}
            inputPlaceholderKey={'invoice.filter.search'}
            localStorageKeyPrefix={'HammockInvoiceOverview'}
        />
    )
}
